/*
 *
 * PosGenericPopUp
 *
 * @version 1.0.0
 * @author Serafino Busalacchi
 * @since 03-Apr-2026
 */

import React from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'
import DatePicker from '@nexidigital/datepicker'
import { Delete } from '@nexidigital/iconlibrary'
import MerchantGenericCard from '@nexidigital/merchantgenericcard'
import MerchantModal from '@nexidigital/merchantmodal'
import PortalButton from '@nexidigital/portalbutton'
import PortalPhoneInput from '@nexidigital/portalphoneinput'
import PortalSelect from '@nexidigital/portalselect'
import PortalSwitch from '@nexidigital/portalswitch'
import PortalTextInput from '@nexidigital/portaltextinput'
import { isEqual } from 'lodash'
import { datePickerEternalMaxDate, momentLongDateFormat, italianDateConverter } from 'Utility/dateMethods'
import { deleteEmptyValues, getLanguageCalendar } from 'Utility/globalMethods'
import { handleOnlyNumberKeyDown, splitPhoneNumber } from 'Utility/numberMethods'
import { isValidHttpUrl, validators } from 'Utility/validatorForm'
import { LANGUAGES_ISO639_2_CODE_FORMAT, SUPPORTED_LANGUAGES } from 'Schemas/constants'
import prefixOptions from 'Schemas/countries.json'
import formattedMessage, { messages } from './languageModule'
import './PosGenericPopUp.scss'

const popupTypes = {
  CONTACT: 'CONTACT',
  ETS: 'ETS',
  WEBSITE: 'WEBSITE',
  PRIVACY_POLICY: 'PRIVACY'
}

const DEFAULT_PRIVACY_POLICY_CATEGORY = 'DEFAULT'
const PRIVACY_POLICY_NAME_MAX_LENGTH = 100

const normalizePrivacyLanguage = (language) => {
  const languageValue = String(language || '').trim()
  return LANGUAGES_ISO639_2_CODE_FORMAT[languageValue.toLowerCase()] || languageValue.toUpperCase()
}

const isDefaultPrivacyLanguage = (language) => normalizePrivacyLanguage(language) === LANGUAGES_ISO639_2_CODE_FORMAT.en

const isPrivacyPolicyFormValid = (values = {}) => {
  const privacyPolicyDetails = Array.isArray(values.privacyPolicyDetail) ? values.privacyPolicyDetail : []
  const privacyLanguages = privacyPolicyDetails.map((detail) => normalizePrivacyLanguage(detail?.language)).filter(Boolean)
  const policyName = String(values.name || '').trim()

  return (
    Boolean(policyName) &&
    policyName.length <= PRIVACY_POLICY_NAME_MAX_LENGTH &&
    privacyPolicyDetails.length > 0 &&
    privacyLanguages.length === privacyPolicyDetails.length &&
    new Set(privacyLanguages).size === privacyLanguages.length &&
    privacyPolicyDetails.every((detail) => String(detail?.text || '').trim())
  )
}

const getContactInitialValues = (values = {}) => {
  if (values.mobilePhoneCustomerCarePrefix || values.mobilePhoneCustomerCareNumber || !values.mobilePhoneCustomerCare) {
    return values
  }

  const { prefix, number } = splitPhoneNumber(values.mobilePhoneCustomerCare, prefixOptions)

  return {
    ...values,
    mobilePhoneCustomerCarePrefix: prefix,
    mobilePhoneCustomerCareNumber: number
  }
}

const PosGenericPopUp = ({ actions, data, selectedPointOfSale, isLoading }) => {
  const intl = useIntl()
  const formatDate = momentLongDateFormat()
  const [focusedPrivacyTextIndex, setFocusedPrivacyTextIndex] = React.useState(null)
  const popupType = (data?.action || '').toUpperCase()
  const sourceValues = data?.editingData || data?.values || {}
  const contactInitialValues = getContactInitialValues(sourceValues)
  const initialValues = popupType === popupTypes.CONTACT ? contactInitialValues : sourceValues
  const hasPolicyIdentity = Boolean(
    sourceValues?.id ||
    sourceValues?.name ||
    sourceValues?.status ||
    sourceValues?.creationDate ||
    sourceValues?.createdAt
  )
  const hasPrivacyPolicyText = Array.isArray(sourceValues?.privacyPolicyDetail)
    ? sourceValues.privacyPolicyDetail.some((policy) => (policy?.text || '').trim().length > 0)
    : false
  const isPrivacyEditMode =
    popupType === popupTypes.PRIVACY_POLICY && (hasPolicyIdentity || hasPrivacyPolicyText)
  
  // Check if this is a default privacy policy
  const isDefaultPolicy =
    String(sourceValues?.category || '').toUpperCase() === DEFAULT_PRIVACY_POLICY_CATEGORY
  const refreshDataAfterUpdate = () => {
    actions.closePopup()
    actions.doGetSubscriptionGlobal?.()
    actions.doFetchPointsOfSales?.()
  }

  const getChangedValues = (values, initialValues) =>
    deleteEmptyValues(
      Object.entries(values || {}).reduce((changedValues, [key, value]) => {
        if (!isEqual(value, initialValues?.[key])) {
          changedValues[key] = value
        }
        return changedValues
      }, {})
    )
  const getPhonePrefix = (values = {}) =>
    values.mobilePhoneCustomerCarePrefix || String(values.mobilePhoneCustomerCare || '').match(/^\+\d+/)?.[0] || ''
  const normalizeContactValues = (values = {}, fallbackValues = {}) => {
    const {
      mobilePhoneCustomerCarePrefix,
      mobilePhoneCustomerCareNumber,
      ...normalizedValues
    } = values

    if (mobilePhoneCustomerCarePrefix || mobilePhoneCustomerCareNumber) {
      normalizedValues.mobilePhoneCustomerCare = `${mobilePhoneCustomerCarePrefix || getPhonePrefix(fallbackValues)}${mobilePhoneCustomerCareNumber || ''}`
    }

    return normalizedValues
  }
  const getPrivacyTextPreview = (text = '', index) => {
    const PRIVACY_TEXT_PREVIEW_MAX_LENGTH = 280
    if (focusedPrivacyTextIndex === index || text.length <= PRIVACY_TEXT_PREVIEW_MAX_LENGTH) {
      return text
    }

    return `${text.slice(0, PRIVACY_TEXT_PREVIEW_MAX_LENGTH).trimEnd()}...`
  }

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      mobilePhoneCustomerCareNumber: Yup.string()
        .min(6, intl.formatMessage(messages.validPhoneNumber))
        .matches(/^\d+$/, intl.formatMessage(messages.validPhoneNumber)),
    }),
    onSubmit: (values) => {
      if (!selectedPointOfSale) {
        actions.closePopup()
        return
      }

      let updatedPointOfSale = selectedPointOfSale
      let updatedDetails = null

      switch (popupType) {
      case popupTypes.CONTACT: {
        const updateValues = getChangedValues(
          normalizeContactValues(values, sourceValues),
          normalizeContactValues(contactInitialValues)
        )
        updatedDetails = updateValues
        updatedPointOfSale = {
          ...selectedPointOfSale,
          details: {
            ...selectedPointOfSale.details,
            ...updateValues
          }
        }
        break
      }
      case popupTypes.ETS: {
        const updateValues = getChangedValues(values, sourceValues)
        if (updateValues.runtsRegistrationDate) {
          updateValues.runtsRegistrationDate = italianDateConverter(moment(values.runtsRegistrationDate, formatDate), 'start')
        }
        updatedDetails = updateValues
        updatedPointOfSale = {
          ...selectedPointOfSale,
          details: {
            ...selectedPointOfSale.details,
            ...updateValues
          }
        }
        break
      }
      case popupTypes.WEBSITE:
        updatedDetails = getChangedValues({ baseUrl: values.websiteUrl }, { baseUrl: sourceValues.websiteUrl })
        actions.doUpdatePointOfSale(
          { pointOfSale: selectedPointOfSale, details: updatedDetails },
          refreshDataAfterUpdate
        )
        return
      case popupTypes.PRIVACY_POLICY:
        if (isDefaultPolicy || !isPrivacyPolicyFormValid(values)) {
          return
        }

        actions.doCreateOrUpdatePrivacyPolicy(
          {
            name: values.name,
            category: 'CUSTOM',
            status: values.isActive ? 'ACTIVE' : 'INACTIVE',
            privacyPolicies: values.privacyPolicyDetail || []
          },
          refreshDataAfterUpdate
        )
        return
      default:
        break
      }

      actions.doSetSelectedPointOfSale(updatedPointOfSale)
      actions.doUpdatePointOfSale(
        { pointOfSale: updatedPointOfSale, ...(updatedDetails && { details: updatedDetails }) },
        refreshDataAfterUpdate
      )
    }
  })

  const emailCustomerCare = String(formik.values.emailCustomerCare || '').trim()
  const mobilePhoneCustomerCareNumber = String(formik.values.mobilePhoneCustomerCareNumber || '').trim()
  const contactHasCompletedField = Boolean(emailCustomerCare || mobilePhoneCustomerCareNumber)
  const emailCustomerCareError = emailCustomerCare ? !validators.isEmail(emailCustomerCare) : false
  const contactSubmitDisabled = !contactHasCompletedField || emailCustomerCareError || formik.errors.mobilePhoneCustomerCareNumber
  const websiteUrlError = formik.values.websiteUrl ? !isValidHttpUrl(formik.values.websiteUrl.trim()) : true

  const modalConfigs = {
    [popupTypes.CONTACT]: {
      title: formattedMessage.contactModalTitle,
      primaryButtonLabel: formattedMessage.confirm,
      secondaryButtonLabel: formattedMessage.cancel
    },
    [popupTypes.ETS]: {
      title: formattedMessage.etsModalTitle,
      primaryButtonLabel: formattedMessage.confirm,
      secondaryButtonLabel: formattedMessage.cancel
    },
    [popupTypes.WEBSITE]: {
      title: formattedMessage.websiteModalTitle,
      primaryButtonLabel: formattedMessage.saveWebsite,
      secondaryButtonLabel: null
    },
    [popupTypes.PRIVACY_POLICY]: {
      title: formattedMessage.privacyModalTitle,
      primaryButtonLabel: isPrivacyEditMode ? formattedMessage.confirm : formattedMessage.create,
      secondaryButtonLabel: null
    },
    DEFAULT: {
      title: formattedMessage.modalTitle,
      primaryButtonLabel: formattedMessage.confirm,
      secondaryButtonLabel: formattedMessage.close
    }
  }

  const config = modalConfigs[popupType] || modalConfigs.DEFAULT
  const privacyPolicyDetails = Array.isArray(formik.values.privacyPolicyDetail) ? formik.values.privacyPolicyDetail : []
  const isPrimaryButtonDisabled =
    (popupType === popupTypes.PRIVACY_POLICY && (isDefaultPolicy || !isPrivacyPolicyFormValid(formik.values))) ||
    (popupType === popupTypes.CONTACT && contactSubmitDisabled) ||
    (popupType === popupTypes.WEBSITE && websiteUrlError)

  const addPrivacyLanguage = () => {
    formik.setFieldValue('privacyPolicyDetail', [...privacyPolicyDetails, { language: '', text: '' }])
  }

  const removePrivacyLanguage = (indexToRemove) => {
    formik.setFieldValue(
      'privacyPolicyDetail',
      privacyPolicyDetails.filter((_, index) => index !== indexToRemove)
    )
  }
  const runtRegistrationDate = moment(formik.values.runtsRegistrationDate, formatDate)

  const getPrivacyLanguageOptions = (currentIndex) => {
    const currentLanguage = normalizePrivacyLanguage(privacyPolicyDetails[currentIndex]?.language)
    const selectedLanguages = new Set(
      privacyPolicyDetails
        .map((detail, index) => (index === currentIndex ? '' : normalizePrivacyLanguage(detail?.language)))
        .filter(Boolean)
    )

    return SUPPORTED_LANGUAGES.map((language) => ({
      value: LANGUAGES_ISO639_2_CODE_FORMAT[language] || language,
      label: intl.formatMessage(messages[language])
    })).filter(({ value }) => {
      const language = normalizePrivacyLanguage(value)
      return language === currentLanguage || !selectedLanguages.has(language)
    })
  }


  const renderFields = () => {

    switch (popupType) {
    case popupTypes.CONTACT:
      return (
       
        <MerchantGenericCard title={formattedMessage.generalInformations}>
          <div className='posGenericPopUp'>
            <PortalTextInput
              id='emailCustomerCare'
              label={formattedMessage.emailCustomerCare}
              value={formik.values.emailCustomerCare || ''}
              onChange={(e) => formik.setFieldValue('emailCustomerCare', e.target.value)}
              onBlur={() => formik.setFieldTouched('emailCustomerCare', true)}
              error={formik.touched.emailCustomerCare && emailCustomerCareError}
              errorText={formattedMessage.emailCustomerCareInvalid}
              placeholderBlank
              type='email'
            />
            <PortalPhoneInput
              id='mobilePhoneCustomerCare'
              pathToDirectFlags={process.env.COUNTRY_FLAGS_URL}
              imageType='svg'
              maxLength={15}
              onPrefixChangeHandler={({ value }) => formik.setFieldValue('mobilePhoneCustomerCarePrefix', value)}
              selectedPrefix={formik.values.mobilePhoneCustomerCarePrefix }
              phonePrefixes={prefixOptions}
              prefixLabel={formattedMessage.mobilePhoneCustomerCare}
              formik={{
                ...formik.getFieldProps('mobilePhoneCustomerCareNumber'),
                onChange: (e) => {
                  const numericValue = String(e?.target?.value || '').replace(/\D/g, '')
                  formik.setFieldValue('mobilePhoneCustomerCareNumber', numericValue)
                }
              }}
              error={formik.errors.mobilePhoneCustomerCareNumber && formik.touched.mobilePhoneCustomerCareNumber}
              errorText={formik.errors.mobilePhoneCustomerCareNumber}
              onKeyDown={handleOnlyNumberKeyDown}
              
            />
          </div>
        </MerchantGenericCard>
        
      )
    case popupTypes.ETS:
      return (
        <MerchantGenericCard title={formattedMessage.generalInformations}>
          <div className='posGenericPopUp'>
            <PortalTextInput
              id='runtsRegistrationCode'
              label={formattedMessage.runtsRegistrationCode}
              value={formik.values.runtsRegistrationCode || ''}
              onChange={(e) => formik.setFieldValue('runtsRegistrationCode', e.target.value)}
              placeholderBlank
            />
            <DatePicker
              placeholder1={' '}
              id1='runtsRegistrationDate'
              locale={getLanguageCalendar()}
              onChange1={(date) =>
                formik.setFieldValue(
                  'runtsRegistrationDate',
                  moment(date, formatDate).startOf('day')
                )
              }
              onBlur1={() => formik.setFieldTouched('runtsRegistrationDate', true)} 
              value1={runtRegistrationDate.isValid() ? runtRegistrationDate.format('YYYY-MM-DD') : ''}
              label1={formattedMessage.runtsRegistrationDate}
              maxDate={datePickerEternalMaxDate}
            />
          </div>
        </MerchantGenericCard>
      )
    case popupTypes.WEBSITE:
      return (
        <MerchantGenericCard subtitle={formattedMessage.websiteModalSubtitle}>
          <div className='posGenericPopUp'>
            <PortalTextInput
              id='websiteUrl'
              label={formattedMessage.websiteUrlLabel}
              value={formik.values.websiteUrl || ''}
              onChange={(e) => formik.setFieldValue('websiteUrl', e.target.value)}
              onBlur={() => formik.setFieldTouched('websiteUrl', true)}
              error={formik.touched.websiteUrl && websiteUrlError}
              errorText={formattedMessage.websiteUrlInvalid}
              placeholder={intl.formatMessage(messages.websiteUrlPlaceholder)}
              placeholderBlank
              type='url'
            />
          </div>
        </MerchantGenericCard>
      )
    case popupTypes.PRIVACY_POLICY:
      return (
        <MerchantGenericCard >
          <div className='posGenericPopUp_privacy'>
            {isDefaultPolicy && (
              <div className='posGenericPopUp_defaultPolicyWarning'>
                <p>{formattedMessage.defaultPolicyRestricted}</p>
              </div>
            )}
            <div className='posGenericPopUp_privacyHeader'>
              <span className='privacyHeader_title'>{formattedMessage.privacyToggleTitle}</span>
              <PortalSwitch
                id='privacy-policy-toggle'
                activeLabel={formattedMessage.inactiveLabel}
                deactiveLabel={formattedMessage.activeLabel}
                checked={!!formik.values.isActive}
                disabled={isDefaultPolicy}
                onChange={(checked) => formik.setFieldValue('isActive', checked)}
              />
            </div>
            <p className='posGenericPopUp_privacySubtitle'>{formattedMessage.privacyToggleSubtitle}</p>

            <PortalTextInput
              id='privacyPolicyName'
              label={formattedMessage.privacyName}
              maxLength={PRIVACY_POLICY_NAME_MAX_LENGTH}
              value={formik.values.name || ''}
              onChange={(e) => formik.setFieldValue('name', e.target.value)}
              disabled={isDefaultPolicy}
              readOnly={isDefaultPolicy}
              placeholderBlank
            />

            <hr className='separator' />

            {privacyPolicyDetails.map((detail, index) => (
              <div className='posGenericPopUp_privacyLanguage' key={`privacy-language-${index}`}>
                <div className='posGenericPopUp_langInput'>
                  <PortalSelect
                    id={`privacyLanguage_${index}`}
                    label={formattedMessage.selectLanguage}
                    options={getPrivacyLanguageOptions(index)}
                    value={detail.language || ''}
                    onChange={(option) => formik.setFieldValue(`privacyPolicyDetail[${index}].language`, option?.value || '')}
                    noSearch
                    placeholderBlank
                    disabled={isDefaultPolicy || isDefaultPrivacyLanguage(detail.language)}
                  />
                </div>
                <div className='posGenericPopUp_privacyText'>
                  <PortalTextInput
                    id={`privacyText_${index}`}
                    label={formattedMessage.privacyText}
                    multiline
                    placeholderBlank
                    value={getPrivacyTextPreview(detail.text || '', index)}
                    onChange={(e) => formik.setFieldValue(`privacyPolicyDetail[${index}].text`, e.target.value)}
                    onFocus={() => setFocusedPrivacyTextIndex(index)}
                    onBlur={() => setFocusedPrivacyTextIndex(null)}
                    disabled={isDefaultPolicy}
                    readOnly={isDefaultPolicy}
                  />
                </div>

                {privacyPolicyDetails.length > 1 && !isDefaultPolicy && detail.language !== 'ENG' && (
                  <div className='posGenericPopUp_removeLanguage'>
                    <PortalButton
                      id={`remove_privacy_language_${index}`}
                      label={formattedMessage.removeLanguage}
                      icon={<Delete size='20' type='line' />}
                      onClick={() => removePrivacyLanguage(index)}
                      size='small'
                      textButton
                      critical
                    />
                  </div>
                )}

                <hr className='separator' />
              </div>
            ))}

            {!isDefaultPolicy && (
              <div className='posGenericPopUp_addLanguage'>
                <PortalButton
                  id='add_privacy_language'
                  label={formattedMessage.addLanguage}
                  onClick={addPrivacyLanguage}
                  size='small'
                  textButton
                />
              </div>
            )}
          </div>
        </MerchantGenericCard>
      )
    }
  }

  return (
    <MerchantModal
      topBarTitle={config.title}
      bigPopup
      onClose={actions.closePopup}
      primaryButtonLabel={config.primaryButtonLabel}
      primaryAction={formik.submitForm}
      primaryButtonDisabled={isPrimaryButtonDisabled}
      primaryButtonLoading={isLoading}
      className='merchantModal'
      {...(config.secondaryButtonLabel && { secondaryButtonLabel: config.secondaryButtonLabel })}
      {...(config.secondaryButtonLabel && { secondaryAction: actions.closePopup })}>
      {renderFields()}
    </MerchantModal>
  )
}

PosGenericPopUp.propTypes = {
  actions: PropTypes.object,
  data: PropTypes.shape({
    section: PropTypes.string,
    action: PropTypes.string,
    editingData: PropTypes.object,
    values: PropTypes.object,
    onSubmit: PropTypes.func
  }),
  selectedPointOfSale: PropTypes.object,
  isLoading: PropTypes.bool
}

PosGenericPopUp.defaultProps = {
  actions: {},
  data: {},
  selectedPointOfSale: null,
  isLoading: false
}

export default React.memo(PosGenericPopUp)
